import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import type { User } from '@supabase/supabase-js';
import Dashboard from './Dashboard';
import JoinPage from './JoinPage';
import RegisterPage from './RegisterPage';
import LearningPublicPage from './pages/LearningPublicPage';
import PsicosocialPublicPage from './pages/PsicosocialPublicPage';
import EnlaceNoDisponible from './components/EnlaceNoDisponible';
import { readAssessmentId } from './lib/psicosocial-formulario';
import PricingPage from './PricingPage';
import PaymentPolicy from './PaymentPolicy';
import PaymentProcessing from './PaymentProcessing';
import LandingPage from './LandingPage';
import { activoDeUrl } from './lib/mantenimiento/mant-qr';
import ResetPassword from './ResetPassword';
import PoliticaPrivacidad from './PoliticaPrivacidad';
import TratamientoDatos from './TratamientoDatos';
import Terms from './Terms';
import { CompanyProvider } from './CompanyContext';
import { ImpersonationProvider } from './ImpersonationContext';
import logoUrl from './assets/storsafe-logo.png';
import './App.css';
import { sendWelcomeEmail, sendAdminNotification } from './lib/email';
import { logAuthEvent } from './auditLog';
import { debeRegistrarLogin } from './lib/login-audit';
import { acceptInvitation, takeInviteToken } from './lib/invitaciones';
import RequiereEmpresa from './components/RequiereEmpresa';

interface PendingRegistration {
  companyName: string;
  ruc: string;
  country: string;
  fullName: string;
  cargo: string;
  phone: string;
  plan?: string;
  cycle?: string;
  skipTrial?: boolean;
}

type RegisterPlan = 'starter' | 'professional' | 'enterprise';

// Flujo B (contratación directa sin trial): /register?plan=starter&cycle=monthly
function getDirectHireParams(): { plan: RegisterPlan; cycle: 'monthly' | 'annual' } | null {
  if (window.location.pathname.toLowerCase() !== '/register') return null;
  const params = new URLSearchParams(window.location.search);
  const plan = params.get('plan');
  if (plan !== 'starter' && plan !== 'professional' && plan !== 'enterprise') return null;
  return { plan, cycle: params.get('cycle') === 'annual' ? 'annual' : 'monthly' };
}

// Rutas que la app reconoce sin sesión (revisado el 26/09/2026: src/, correos
// de send-email e invite-user, Supabase Auth, PagoPlux, vercel.json). Una ruta
// fuera de la lista NO se bloquea: se muestra el login con un aviso discreto.
const KNOWN_PATHS = new Set([
  '/', '/register', '/pricing', '/politica-de-pagos', '/pago-procesando',
  '/reset-password', '/politica-de-privacidad', '/tratamiento-de-datos',
  '/terminos-y-condiciones', '/join',
]);

function currentPath(): string {
  const p = window.location.pathname.toLowerCase().replace(/\/+$/, '');
  return p === '' ? '/' : p;
}

function App() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState<User | null>(null);
  // true cuando ya se sabe si hay sesión (JoinPage decide su pantalla con eso)
  const [authReady, setAuthReady] = useState(false);
  const prevUserIdRef = useRef<string | null>(null);

  // Invitación: ?join=TOKEN (enlace copiado en Usuarios) o /join?token=TOKEN
  // (botón del correo de invite-user desde 659d078). Se lee al primer render.
  const [joinToken] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('join') ?? (currentPath() === '/join' ? params.get('token') : null);
  });
  const [learningId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('learning')
  );
  const [psicosocialAssessmentId] = useState<string | null>(
    () => readAssessmentId(window.location.search)
  );
  // ?psicosocial= presente pero con un valor que no es un UUID válido.
  const [psicosocialParamInvalid] = useState(
    () => new URLSearchParams(window.location.search).has('psicosocial') && readAssessmentId(window.location.search) === null
  );
  const [unknownPath] = useState(() => !KNOWN_PATHS.has(currentPath()));

  // Flujo B: contratación directa sin trial — /register?plan=&cycle=
  const [directHire] = useState(() => getDirectHireParams());

  // Public pages (no auth required) — read pathname synchronously
  const [showRegister, setShowRegister] = useState(() => !!directHire);
  const [showPricing,  setShowPricing]  = useState(
    () => window.location.pathname.toLowerCase() === '/pricing'
  );
  const [showPaymentPolicy, setShowPaymentPolicy] = useState(
    () => window.location.pathname.toLowerCase() === '/politica-de-pagos'
  );
  const [showPaymentProcessing] = useState(
    () => window.location.pathname.toLowerCase() === '/pago-procesando'
  );
  const [showLanding, setShowLanding] = useState(
    () => {
      const p = window.location.pathname.toLowerCase();
      // Mantenimiento M1: el enlace de un QR (?activo=<id>) sin sesión va directo al login, sin mostrar datos.
      return (p === '/' || p === '') && !activoDeUrl(window.location.search);
    }
  );
  const [selectedPlan, setSelectedPlan] = useState<RegisterPlan>(() => directHire?.plan ?? 'starter');

  // Password visibility & forgot-password modal
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  // Public pages routed by URL path
  const [showResetPassword] = useState(
    () => window.location.pathname.toLowerCase() === '/reset-password'
  );
  const [showPoliticaPrivacidad, setShowPoliticaPrivacidad] = useState(
    () => window.location.pathname.toLowerCase() === '/politica-de-privacidad'
  );
  const [showTratamientoDatos, setShowTratamientoDatos] = useState(
    () => window.location.pathname.toLowerCase() === '/tratamiento-de-datos'
  );
  const [showTermsPublic, setShowTermsPublic] = useState(
    () => window.location.pathname.toLowerCase() === '/terminos-y-condiciones'
  );

  // Magic-link invite flow — read hash synchronously
  const [isInviteFlow, setIsInviteFlow] = useState(
    () => window.location.hash.includes('type=invite')
  );
  const [inviteName, setInviteName]     = useState('');
  const [invitePass, setInvitePass]     = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError]   = useState('');

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
      setAuthReady(true);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Complete pending operations BEFORE setting user, so Dashboard renders
      // with profile already created.
      if (session?.user) {
        // 1. Public registration flow (email confirmation path)
        const regRaw = sessionStorage.getItem('pending_registration');
        if (regRaw) {
          try {
            const reg = JSON.parse(regRaw) as PendingRegistration;
            const { error: regError } = await supabase.rpc('register_company', {
              p_company_name:  reg.companyName,
              p_ruc:           reg.ruc   || null,
              p_country:       reg.country,
              p_full_name:     reg.fullName,
              p_cargo:         reg.cargo || null,
              p_phone:         reg.phone || null,
              p_plan:          reg.plan  || null,
              p_billing_cycle: reg.skipTrial ? (reg.cycle ?? 'monthly') : null,
              p_skip_trial:    !!reg.skipTrial,
            });
            if (!regError && session.user.email) {
              // El servidor toma destinatario y datos de la base (A5)
              sendWelcomeEmail();
              sendAdminNotification();
            }
          } finally {
            sessionStorage.removeItem('pending_registration');
          }
        }

        // 2. Invitación pendiente (alta con confirmación de correo, aunque el
        //    enlace se abra en otra pestaña): se acepta en el servidor con la
        //    RPC accept_invitation antes de fijar el usuario, igual que el
        //    registro. Si falla, la persona ve "tu cuenta no tiene empresa" y
        //    puede volver a abrir el enlace de la invitación.
        const inviteToken = takeInviteToken();
        if (inviteToken) {
          const res = await acceptInvitation(inviteToken);
          if (!res.ok) console.error('No se pudo aceptar la invitación pendiente:', res.code);
        }
      }

      // SIGNED_IN llega también al recargar, al volver a la pestaña y desde
      // otras pestañas: un solo user_login por sesión (§17 ítem 73).
      if (session?.user && debeRegistrarLogin(event, session.user.id, session.access_token)) {
        logAuthEvent('user_login', { email: session.user.email });
      }
      if (event === 'SIGNED_OUT') {
        logAuthEvent('user_logout', { user_id: prevUserIdRef.current });
      }
      prevUserIdRef.current = session?.user?.id ?? null;

      setUser(session?.user ?? null);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });

    if (authError) {
      setError(translateError(authError.message));
    }

    setLoading(false);
  };

  const handleAcceptInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setInviteError('');
    setInviteLoading(true);

    const meta = user.user_metadata as {
      invitation_token?: string;
    };

    // 1. Set password and full name
    const { error: updateErr } = await supabase.auth.updateUser({
      password: invitePass,
      data: { full_name: inviteName.trim() },
    });

    if (updateErr) {
      setInviteError('Error al establecer contraseña: ' + updateErr.message);
      setInviteLoading(false);
      return;
    }

    // 2. Perfil + invitación aceptada en el servidor (rol y empresa salen de
    //    la invitación, no de user_metadata, que el usuario puede editar).
    if (meta.invitation_token) {
      const res = await acceptInvitation(meta.invitation_token);
      if (!res.ok) {
        setInviteError(res.message);
        setInviteLoading(false);
        return;
      }
    }

    setIsInviteFlow(false);
    setInviteLoading(false);
  };

  const handleForgotPassword = async () => {
    setForgotLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: 'https://storsafe.app/reset-password',
    });
    setForgotMessage(resetError
      ? 'Si ese correo está registrado, recibirás un enlace en breve.'
      : 'Revisa tu correo — te enviamos un enlace para restablecer tu contraseña.'
    );
    setForgotLoading(false);
  };

  // ── Public Learning page (?learning=ID) ──────────────────────────────────
  if (learningId) {
    return <LearningPublicPage learningId={learningId} />;
  }

  // ── Public Psicosocial questionnaire (?psicosocial=ID#c=CODE) ───────────
  if (psicosocialAssessmentId) {
    return <PsicosocialPublicPage assessmentId={psicosocialAssessmentId} />;
  }
  if (psicosocialParamInvalid) {
    return <EnlaceNoDisponible message="El enlace de la evaluación no es válido. Ábralo de nuevo desde el mensaje o el código QR que recibió." />;
  }

  // ── Custom token join page (?join=TOKEN) ────────────────────────────────
  // También con sesión abierta: JoinPage ofrece aceptar con esa cuenta o
  // cerrar sesión si la invitación es para otro correo.
  if (joinToken) {
    if (!authReady) return null;
    return <JoinPage token={joinToken} user={user} />;
  }

  // ── Password reset page (linked from email) ─────────────────────────────
  if (showResetPassword) {
    return <ResetPassword />;
  }

  // ── Legal public pages ───────────────────────────────────────────────────
  if (showPoliticaPrivacidad) {
    return <PoliticaPrivacidad onBack={() => setShowPoliticaPrivacidad(false)} />;
  }
  if (showTratamientoDatos) {
    return <TratamientoDatos onBack={() => setShowTratamientoDatos(false)} />;
  }
  if (showTermsPublic) {
    return <Terms onBack={() => setShowTermsPublic(false)} />;
  }

  // ── Payment policy page (public, no auth required) ──────────────────────
  if (showPaymentPolicy) {
    return <PaymentPolicy onBack={() => setShowPaymentPolicy(false)} />;
  }

  // ── Payment processing confirmation (public, no auth required) ──────────
  if (showPaymentProcessing) {
    return <PaymentProcessing />;
  }

  // ── Pricing page (pública, o logueada para pago directo / trial vencido) ──
  if (showPricing) {
    return (
      <PricingPage
        onLogin={() => setShowPricing(false)}
        onRegister={(plan) => { setSelectedPlan(plan); setShowPricing(false); setShowRegister(true); }}
        userId={user?.id}
      />
    );
  }

  // ── Public registration page ─────────────────────────────────────────────
  if (showRegister && !user) {
    return (
      <RegisterPage
        initialPlan={selectedPlan}
        initialCycle={directHire?.cycle}
        skipTrial={!!directHire}
        onBack={() => setShowRegister(false)}
      />
    );
  }

  // ── Supabase magic-link invite acceptance ────────────────────────────────
  if (isInviteFlow && user) {
    return (
      <div className="login-root">
        <div className="login-card">
          <div className="login-logo">
            <img src={logoUrl} alt="StorSafe" className="login-logo-img" />
          </div>
          <p className="login-tagline">Configura tu cuenta para continuar</p>

          <form className="login-form" onSubmit={handleAcceptInvite}>
            <div className="field-group">
              <label htmlFor="inv-name">Tu nombre</label>
              <div className="input-wrapper">
                <UserIcon />
                <input
                  id="inv-name"
                  type="text"
                  placeholder="Juan Pérez"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  autoFocus
                  disabled={inviteLoading}
                />
              </div>
            </div>

            <div className="field-group">
              <label htmlFor="inv-pass">Crea una contraseña <span style={{ color: '#ef4444' }}>*</span></label>
              <div className="input-wrapper">
                <LockIcon />
                <input
                  id="inv-pass"
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  value={invitePass}
                  onChange={(e) => setInvitePass(e.target.value)}
                  minLength={8}
                  required
                  disabled={inviteLoading}
                />
              </div>
            </div>

            {inviteError && <p className="login-error">{inviteError}</p>}

            <button type="submit" className="btn-login" disabled={inviteLoading || invitePass.length < 8}>
              {inviteLoading ? <span className="btn-spinner" /> : 'Activar cuenta'}
            </button>
          </form>

          <p className="login-footer">© 2026 StorSafe · Todos los derechos reservados</p>
        </div>
      </div>
    );
  }

  // ── Authenticated view ──────────────────────────────────────────────────
  if (user) {
    return (
      <CompanyProvider user={user}>
        <ImpersonationProvider>
          <RequiereEmpresa user={user}>
            <Dashboard user={user} />
          </RequiereEmpresa>
        </ImpersonationProvider>
      </CompanyProvider>
    );
  }

  // ── Landing page (homepage, no auth required) ───────────────────────────
  if (!user && showLanding) {
    return (
      <LandingPage
        onLogin={() => setShowLanding(false)}
        onRegister={() => { setShowLanding(false); setShowRegister(true); }}
        onPricing={() => { setShowLanding(false); setShowPricing(true); }}
      />
    );
  }

  // ── Login form ──────────────────────────────────────────────────────────
  return (
    <>
    {showForgotModal && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 32, width: '100%', maxWidth: 400, margin: '0 16px' }}>
          <h3 style={{ color: '#e2e8f0', margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 700 }}>Recuperar contraseña</h3>
          <p style={{ color: '#94a3b8', margin: '0 0 20px', fontSize: '0.88rem' }}>Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.</p>
          {forgotMessage ? (
            <p style={{ color: '#2FBF71', fontSize: '0.88rem', textAlign: 'center', margin: '0 0 16px', lineHeight: 1.5 }}>{forgotMessage}</p>
          ) : (
            <>
              <input
                type="email"
                placeholder="usuario@empresa.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                autoFocus
                style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: '12px 14px', fontSize: '0.95rem', color: '#e2e8f0', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
              />
              <button
                onClick={handleForgotPassword}
                disabled={forgotLoading || !forgotEmail}
                style={{ width: '100%', padding: '13px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)', color: '#fff', fontWeight: 700, border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: '0.95rem', marginBottom: 10, opacity: (forgotLoading || !forgotEmail) ? 0.6 : 1 }}
              >
                {forgotLoading ? 'Enviando…' : 'Enviar enlace'}
              </button>
            </>
          )}
          <button
            onClick={() => setShowForgotModal(false)}
            style={{ width: '100%', padding: '11px', background: 'none', border: '1px solid #334155', borderRadius: 10, color: '#64748b', cursor: 'pointer', fontSize: '0.88rem' }}
          >
            {forgotMessage ? 'Cerrar' : 'Cancelar'}
          </button>
        </div>
      </div>
    )}
    <div className="login-root">
      <div className="login-card">
        <div className="login-logo">
          <img src={logoUrl} alt="StorSafe" className="login-logo-img" />
        </div>

        <p className="login-tagline">Gestión Segura de Bodegas Químicas</p>

        {unknownPath && (
          <p style={{ color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center', margin: '0 0 16px' }}>
            No encontramos esa página. Inicia sesión o <a href="/" style={{ color: '#2FBF71' }}>vuelve al inicio</a>.
          </p>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="field-group">
            <label htmlFor="email">Correo electrónico</label>
            <div className="input-wrapper">
              <EmailIcon />
              <input
                id="email"
                type="email"
                placeholder="usuario@empresa.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>
          </div>

          <div className="field-group">
            <label htmlFor="password">Contraseña</label>
            <div className="input-wrapper">
              <LockIcon />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                required
                autoComplete="current-password"
                disabled={loading}
                style={{ paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                style={{ position: 'absolute', right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4, display: 'flex', alignItems: 'center' }}
                tabIndex={-1}
              >
                {showPassword ? <EyeOffIcon /> : <EyeOnIcon />}
              </button>
            </div>
          </div>

          {error && <p className="login-error">{error}</p>}

          <div className="forgot-row">
            <button
              type="button"
              className="forgot-link"
              onClick={() => { setForgotEmail(email); setForgotMessage(''); setShowForgotModal(true); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <button type="submit" className="btn-login" disabled={loading}>
            {loading ? <span className="btn-spinner" /> : 'Ingresar'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.84rem', color: '#64748b' }}>
          ¿No tienes cuenta?{' '}
          <button
            onClick={() => setShowRegister(true)}
            style={{ background: 'none', border: 'none', color: '#2FBF71', cursor: 'pointer', fontSize: '0.84rem', fontWeight: 600, padding: 0 }}
          >
            Regístrate gratis
          </button>
          {' · '}
          <button
            onClick={() => setShowPricing(true)}
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.84rem', padding: 0 }}
          >
            Ver planes
          </button>
        </p>

        <div style={{ marginTop: 18, textAlign: 'center' }}>
          <p style={{ fontSize: '0.72rem', color: '#475569', margin: '0 0 5px' }}>© 2026 StorSafe · AndeanSoft SAS</p>
          <p style={{ fontSize: '0.72rem', color: '#475569', margin: 0, lineHeight: 1.8 }}>
            <a href="/politica-de-privacidad" target="_blank" rel="noopener noreferrer" className="forgot-link">Política de Privacidad</a>
            {' · '}
            <a href="/terminos-y-condiciones" target="_blank" rel="noopener noreferrer" className="forgot-link">Términos de Uso</a>
            {' · '}
            <a href="/tratamiento-de-datos" target="_blank" rel="noopener noreferrer" className="forgot-link">Tratamiento de Datos</a>
          </p>
        </div>
      </div>
    </div>
    </>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function translateError(msg: string): string {
  if (msg.includes('Invalid login credentials')) return 'Correo o contraseña incorrectos.';
  if (msg.includes('Email not confirmed')) return 'Debes confirmar tu correo antes de ingresar.';
  if (msg.includes('Too many requests')) return 'Demasiados intentos. Espera unos minutos.';
  if (msg.includes('Network')) return 'Sin conexión. Verifica tu internet.';
  return 'Ocurrió un error. Intenta nuevamente.';
}

// ── SVG icons as components to keep JSX clean ───────────────────────────────

function UserIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="input-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

function EyeOnIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default App;
